import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  MessageCircle, 
  Video, 
  MapPin, 
  Heart,
  Calendar,
  Briefcase,
  GraduationCap,
  Music,
  Camera,
  Hourglass,
  Flag
} from 'lucide-react';
import UpgradeModal from './UpgradeModal';
import ReportUserDialog from './ReportUserDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MatchProfile = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [matchUser, setMatchUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [userTier, setUserTier] = useState('free');
  const [showReport, setShowReport] = useState(false);

  // Elegant glassmorphism queue notification
  const showQueueNotification = (partnerName, position) => {
    const modal = document.createElement('div');
    modal.id = 'queue-notification-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 27, 75, 0.92));
      backdrop-filter: blur(20px);
      display: flex;
      align-items: center;
      justify-center: center;
      z-index: 999999;
      padding: 20px;
    `;
    
    modal.innerHTML = `
      <div style="width: 100%; max-width: 28rem;">
        <div style="
          backdrop-filter: blur(60px);
          background: linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%);
          border-radius: 2rem;
          padding: 2.5rem;
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1);
        ">
          <div style="text-align: center;">
            
            <!-- Hourglass Icon -->
            <div style="display: inline-flex; align-items: center; justify-content: center; margin-bottom: 1.5rem;">
              <div style="width: 5rem; height: 5rem; border-radius: 50%; border: 2px solid rgba(244, 114, 182, 0.2); position: relative;">
                <div style="position: absolute; inset: 0.75rem; border-radius: 50%; background: linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(236, 72, 153, 0.15)); display: flex; align-items: center; justify-content: center;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(251, 207, 232, 0.8)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: rotate 2s linear infinite;">
                    <path d="M5 22h14"/>
                    <path d="M5 2h14"/>
                    <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/>
                    <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/>
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Title -->
            <div style="margin-bottom: 1.5rem;">
              <h2 style="font-size: 1.5rem; font-weight: 300; color: white; letter-spacing: 0.025em; margin-bottom: 0.5rem;">
                Currently Unavailable
              </h2>
              <p style="color: rgba(255,255,255,0.6); font-size: 1rem; font-weight: 300; line-height: 1.625;">
                ${partnerName} is in another date right now
              </p>
            </div>
            
            <!-- Queue Position Badge -->
            <div style="
              backdrop-filter: blur(16px);
              background: linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
              border-radius: 0.75rem;
              padding: 1rem;
              border: 1px solid rgba(255,255,255,0.06);
              margin-bottom: 1.5rem;
            ">
              <p style="color: rgba(255,255,255,0.4); font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.25rem;">Your Position</p>
              <p style="color: white; font-size: 3rem; font-weight: 300; line-height: 1;">#${position}</p>
              <p style="color: rgba(255,255,255,0.5); font-size: 0.875rem; margin-top: 0.5rem;">We'll notify you when they're available</p>
            </div>
            
            <!-- Action Button -->
            <div style="padding-top: 0.5rem;">
              <button id="queue-notification-close" style="
                width: 100%;
                position: relative;
                overflow: hidden;
                border-radius: 0.75rem;
                background: linear-gradient(135deg, rgba(236, 72, 153, 0.85), rgba(239, 68, 68, 0.85));
                box-shadow: 0 8px 24px rgba(236, 72, 153, 0.2);
                border: none;
                padding: 0.875rem;
                color: white;
                font-weight: 500;
                letter-spacing: 0.025em;
                cursor: pointer;
                font-size: 1rem;
                transition: all 0.3s ease;
              ">
                <span style="position: relative; z-index: 1;">Got It</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <style>
        @keyframes rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(180deg); }
        }
        #queue-notification-close:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(236, 72, 153, 0.3);
        }
      </style>
    `;
    
    document.body.appendChild(modal);
    
    const closeBtn = document.getElementById('queue-notification-close');
    if (closeBtn) {
      closeBtn.onclick = () => modal.remove();
    }
    
    modal.onclick = (e) => {
      if (e.target === modal) modal.remove();
    };
  };

  useEffect(() => {
    fetchMatchProfile();
    fetchUserTier();
  }, [matchId]);

  const fetchUserTier = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUserTier(userData.subscription_tier || 'free');
      }
    } catch (error) {
      console.error('Error fetching user tier:', error);
    }
  };

  const fetchMatchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const timestamp = new Date().getTime();
      
      // Fetch match details - use the same endpoint as MatchingInterface
      const matchResponse = await fetch(`${BACKEND_URL}/api/user-matches/confirmed?t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!matchResponse.ok) {
        throw new Error('Failed to fetch matches');
      }

      const matchesData = await matchResponse.json();
      console.log('🔍 Matches API Response:', matchesData);
      
      // Extract matches array
      const matchesArray = matchesData.matches || [];
      console.log('🔍 Matches Array:', matchesArray);
      console.log('🔍 Looking for matchId:', matchId);
      
      // Search by match_id (the field returned by the API)
      const currentMatch = matchesArray.find(m => m.match_id === matchId);
      console.log('🔍 Found match:', currentMatch);

      if (!currentMatch) {
        console.error('❌ Match not found. Available match IDs:', matchesArray.map(m => m.match_id));
        setError('Match not found');
        setLoading(false);
        return;
      }

      setMatch(currentMatch);
      setMatchUser(currentMatch.user);

      // Fetch photos for the matched user
      const photosResponse = await fetch(`${BACKEND_URL}/api/users/${currentMatch.user.id}/photos`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (photosResponse.ok) {
        const photosData = await photosResponse.json();
        setPhotos(photosData.photos || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching match profile:', error);
      setError('Failed to load profile');
      setLoading(false);
    }
  };

  const handleChatClick = async () => {
    if (userTier === 'free') {
      setUpgradeFeature('chat');
      setShowUpgradeModal(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      // Get or create conversation for this match
      const response = await fetch(
        `${BACKEND_URL}/api/chat/match/${match.match_id}/conversation`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Navigate to the conversation
        navigate(`/chat/${data.conversation_id}`);
      } else if (response.status === 403) {
        // Backend confirmed user is free tier - show upgrade modal
        setUpgradeFeature('chat');
        setShowUpgradeModal(true);
      } else {
        alert('Failed to start chat. Please try again.');
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      alert('Failed to start chat. Please try again.');
    }
  };

  const handleVideoClick = async () => {
    if (userTier === 'free') {
      setUpgradeFeature('video');
      setShowUpgradeModal(true);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Request speed date via queue system
      const response = await fetch(`${BACKEND_URL}/api/speed-dating/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          match_id: match.match_id
        })
      });
      
      const result = await response.json();
      
      // Check for email verification required error
      if (response.status === 403 && result.detail?.action === 'verify_email') {
        alert('📧 Email Verification Required\n\nPlease verify your email to start speed dating. Check your inbox for the confirmation link.');
        return;
      }
      
      if (!response.ok) {
        throw new Error(result.detail?.message || result.detail || 'Failed to start speed date');
      }
      
      if (result.queued) {
        // User was queued - show elegant glassmorphism modal
        showQueueNotification(matchUser.name, result.position);
      } else if (result.requires_upgrade) {
        // Free user hit queue limit (shouldn't happen since we check tier above)
        alert(result.message);
      } else if (result.ready_to_start) {
        // Can start immediately
        navigate(`/speed-dating-demo?partner=${matchUser.id}`);
      }
    } catch (error) {
      console.error('Error starting video call:', error);
      alert(error.message || 'Failed to start video call. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !matchUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-white text-xl mb-4">{error || 'Profile not found'}</p>
          <button
            onClick={() => navigate('/matches')}
            className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-3 rounded-xl"
          >
            Back to Matches
          </button>
        </div>
      </div>
    );
  }

  const displayPhotos = photos.length > 0 ? photos : [{ url: matchUser.photo_url }].filter(p => p.url);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80 overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/30 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/matches')}
            className="flex items-center space-x-2 text-white hover:text-rose-400 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Matches</span>
          </button>
          
          <div className="flex items-center space-x-2">
            {match?.both_liked && (
              <div className="flex items-center space-x-1 bg-rose-500/20 px-3 py-1 rounded-full">
                <Heart className="h-4 w-4 text-rose-400 fill-rose-400" />
                <span className="text-rose-300 text-sm">Mutual Match</span>
              </div>
            )}
            {matchUser && (
              <button
                onClick={() => setShowReport(true)}
                data-testid="profile-report-btn"
                className="flex items-center space-x-1 text-white/70 hover:text-rose-300 px-3 py-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <Flag className="h-4 w-4" />
                <span className="text-sm">Report</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <ReportUserDialog
        open={showReport}
        user={matchUser}
        onClose={() => setShowReport(false)}
        onReported={() => navigate('/matches')}
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Column - Photos & Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo Gallery */}
            <div className="bg-black/20 backdrop-blur-2xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
              {displayPhotos.length > 0 ? (
                <>
                  {/* Main Photo */}
                  <div className="aspect-[3/4] relative">
                    <img
                      src={`${BACKEND_URL}${displayPhotos[selectedPhotoIndex].url}`}
                      alt={matchUser.name}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Photo Counter */}
                    {displayPhotos.length > 1 && (
                      <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm flex items-center space-x-1">
                        <Camera className="h-4 w-4" />
                        <span>{selectedPhotoIndex + 1} / {displayPhotos.length}</span>
                      </div>
                    )}
                  </div>

                  {/* Photo Thumbnails */}
                  {displayPhotos.length > 1 && (
                    <div className="grid grid-cols-6 gap-2 p-4 bg-black/30">
                      {displayPhotos.map((photo, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedPhotoIndex(idx)}
                          className={`aspect-square rounded-lg overflow-hidden ring-2 transition-all ${
                            idx === selectedPhotoIndex
                              ? 'ring-rose-400 scale-105'
                              : 'ring-white/20 opacity-60 hover:opacity-100'
                          }`}
                        >
                          <img
                            src={`${BACKEND_URL}${photo.url}`}
                            alt={`${matchUser.name} ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="aspect-[3/4] bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center">
                  <div className="text-white text-6xl font-bold">
                    {matchUser.name?.charAt(0) || 'U'}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleChatClick}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white py-4 rounded-2xl flex items-center justify-center space-x-2 transition-all transform hover:scale-105 shadow-lg"
              >
                <MessageCircle className="h-5 w-5" />
                <span className="text-lg font-medium">
                  {userTier === 'free' ? 'Unlock Chat' : 'Start Chat'}
                </span>
              </button>

              <button
                onClick={handleVideoClick}
                className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-4 rounded-2xl flex items-center justify-center space-x-2 transition-all transform hover:scale-105 shadow-lg"
              >
                <Video className="h-5 w-5" />
                <span className="text-lg font-medium">
                  {userTier === 'free' ? 'Unlock Video Call' : 'Video Call'}
                </span>
              </button>
            </div>
          </div>

          {/* Right Column - Profile Info */}
          <div className="lg:col-span-3 space-y-6">
            {/* Header Info */}
            <div className="bg-black/20 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl">
              <h1 className="text-4xl font-light text-white mb-2">
                {matchUser.name}
                {matchUser.age && <span className="text-gray-300">, {matchUser.age}</span>}
              </h1>
              
              {matchUser.location && (
                <div className="flex items-center space-x-2 text-gray-300 mb-4">
                  <MapPin className="h-4 w-4" />
                  <span>{matchUser.location}</span>
                </div>
              )}

              {/* Bio */}
              {matchUser.bio && (
                <div className="mt-6">
                  <h3 className="text-xl font-medium text-white mb-3">About</h3>
                  <p className="text-gray-300 leading-relaxed">{matchUser.bio}</p>
                </div>
              )}
            </div>

            {/* Interests */}
            {matchUser.interests && matchUser.interests.length > 0 && (
              <div className="bg-black/20 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                <div className="flex items-center space-x-2 mb-4">
                  <Music className="h-5 w-5 text-rose-400" />
                  <h3 className="text-xl font-medium text-white">Interests</h3>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(matchUser.interests) ? matchUser.interests : matchUser.interests.split(',')).map((interest, idx) => (
                    <span
                      key={idx}
                      className="bg-rose-500/20 text-rose-300 border border-rose-400/30 px-4 py-2 rounded-full text-sm"
                    >
                      {typeof interest === 'string' ? interest.trim() : interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Info */}
            <div className="bg-black/20 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl">
              <h3 className="text-xl font-medium text-white mb-4">Details</h3>
              
              <div className="space-y-3">
                {matchUser.age && (
                  <div className="flex items-center space-x-3">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-300">{matchUser.age} years old</span>
                  </div>
                )}
                
                {matchUser.location && (
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-300">{matchUser.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-rose-500/20 to-pink-500/20 backdrop-blur-2xl border border-rose-400/30 rounded-3xl p-8 shadow-2xl">
              <h3 className="text-2xl font-medium text-white mb-3">Ready to Connect?</h3>
              <p className="text-gray-300 mb-6">
                Start a conversation and see where it goes!
              </p>
              
              <button
                onClick={() => navigate('/matches')}
                className="text-rose-300 hover:text-rose-200 transition-colors"
              >
                ← Browse More Matches
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature={upgradeFeature}
        />
      )}
    </div>
  );
};

export default MatchProfile;
